import React, { useMemo } from 'react';
import { getVendor } from '../../config/vendorRegistry';
import { resolveOfferedProducts } from '../../catalog/productOffering';
import { groupByFamily } from '../../catalog/productFamilies';
import { formatDollars } from './format';

const STATUS_STYLE = {
  offerable:   'text-green-400',
  hidden:      'text-yellow-400',
  future:      'text-blue-400',
  unreachable: 'text-orange-400',
  unpriced:    'text-red-400',
  retired:     'text-gray-500',
};

/**
 * SP4: every hardware SKU the configurator can put on a non-CCI BOM, grouped by
 * product family. READ-ONLY by design — the offer set lives in vendorRegistry
 * source, and changing it stays a reviewed code change.
 */
export function ProductsView({ vendor, quarter, seed, productDb }) {
  const families = getVendor(vendor).capabilities.productFamilies;
  // The vendor's productDb lives at `databases.products` — verified, NOT
  // `.productDb`. The `products` key here is the whole productDb JSON; its own
  // `.products` is the SKU map inside it.
  const db = productDb ?? getVendor(vendor).databases.products;

  /**
   * `resolveOfferedProducts` calls `snapshotFromSeed`, which THROWS BY DESIGN
   * (seedBuilder.ts:294-303) when a cost row exists for a quarter with no
   * matching global integrationPct/CMP-DISC-HW row — a real authoring gap a
   * backdated CSV import or manual edit can produce for any offered quarter.
   * Products is now the LANDING tab, so an unguarded throw here would take
   * down the whole `/admin/catalog` page, including the Impact tab a PM would
   * need to fix the gap. Every other UI consumer of the engine already treats
   * this as a recoverable, surfaced error rather than a crash — see
   * `useCatalogDraft.js:118-127` and `:544-553` (`impactError`, documented at
   * `:96-98`: "a failure to compute ... is not a throw ... because the plan
   * is still sound and still worth showing"). This mirrors that.
   */
  const { groups, error } = useMemo(() => {
    try {
      const offered = resolveOfferedProducts(vendor, quarter, { productDb: db, seed });
      return { groups: groupByFamily(offered, families), error: null };
    } catch (e) {
      console.error('Product catalog resolution failed:', e);
      return { groups: [], error: e instanceof Error ? e.message : String(e) };
    }
  }, [vendor, quarter, seed, db, families]);

  if (error) {
    return <div role="alert" className="text-red-400 text-sm mb-2">{error}</div>;
  }

  return (
    <table className="w-full text-sm" aria-label={`Offered products for ${quarter}`}>
      <thead>
        <tr className="text-left text-gray-400">
          <th scope="col" className="px-2">SKU</th>
          <th scope="col" className="px-2">Status</th>
          <th scope="col" className="px-2 text-right">Price</th>
          <th scope="col" className="px-2 text-right">Cost</th>
          <th scope="col" className="px-2">Basis</th>
          <th scope="col" className="px-2">Governed by</th>
          <th scope="col" className="px-2">Reached by</th>
        </tr>
      </thead>
      {groups.map((group) => (
        <tbody key={group.id} aria-label={group.label}>
          <tr>
            <th scope="colgroup" colSpan={7} className="px-2 pt-3 text-left font-semibold text-gray-300">
              {group.label}
            </th>
          </tr>
          {group.offerings.map((o) => (
            <tr key={o.sku}>
              <th scope="row" className="px-2 font-normal">{o.sku}</th>
              <td className={`px-2 ${STATUS_STYLE[o.status] ?? ''}`} aria-label={`${o.sku} status`}>
                {o.status}{o.availableFrom ? ` (${o.availableFrom})` : ''}
              </td>
              <td className="px-2 text-right" aria-label={`${o.sku} price`}>
                {formatDollars(o.storedPriceCents === null ? null : o.storedPriceCents / 100)}
              </td>
              <td className="px-2 text-right" aria-label={`${o.sku} cost`}>
                {formatDollars(o.costMicros === null ? null : o.costMicros / 1e6)}
              </td>
              {/* A cost we do not have has no basis to report. "lump" would
                  claim we know how this SKU's cost is put together, of a SKU
                  whose cost is unknown — precisely the claim the resolver
                  refuses to make (productOffering.js: no composition means
                  null cost AND not decomposed). Same rule as the cost cell
                  beside it: unknown renders as a dash, never as a verdict. */}
              <td className="px-2" aria-label={`${o.sku} basis`}>
                {o.costMicros === null ? '—' : (o.decomposed ? 'decomposed' : 'lump')}
              </td>
              <td className="px-2 text-gray-400">{o.governedBy}</td>
              <td className="px-2 text-gray-400">{o.reachedBy.join('; ') || '—'}</td>
            </tr>
          ))}
        </tbody>
      ))}
    </table>
  );
}

export default ProductsView;
